import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { orderApi } from '@formosa/shared/api/order';
import { normalizeSymbolCode } from '@formosa/shared/utils/marketThumb';
import FuturesPositionCard from '../../components/trade/FuturesPositionCard';
import PerpetualEntrustmentCard from '../../components/trade/PerpetualEntrustmentCard';
import PerpetualPositionCard from '../../components/trade/PerpetualPositionCard';
import TimedPositionCard from '../../components/trade/TimedPositionCard';
import WalletPositionHistoryItem from '../../components/funds/WalletPositionHistoryItem';
import { useWallet } from '../../context/WalletContext';
import useMarketThumbSocket from '../../hooks/useMarketThumbSocket';
import { useTranslation } from '../../i18n/LocaleContext';
import {
  ORDER_PLAY_TABS,
  ORDER_SUB_TABS,
  buildProductMap,
  fetchOrderPage,
  getDefaultSubTab,
  resolveOrderProduct,
  resolveSubTab,
} from './orderPageConfig';

const PAGE_SIZE = 15;

function collectSymbolCodes(orders) {
  if (!Array.isArray(orders)) {
    return [];
  }
  const codes = orders
    .map((order) => normalizeSymbolCode(order?.marketSymbolCode || ''))
    .filter(Boolean);
  return [...new Set(codes)];
}

export default function OrdersPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { balance, refreshWallet } = useWallet();

  const [playTab, setPlayTab] = useState('timed');
  const [subTab, setSubTab] = useState(getDefaultSubTab('timed'));
  const [records, setRecords] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [productMaps, setProductMaps] = useState({
    timed: new Map(),
    futures: new Map(),
    perpetual: new Map(),
  });
  const [actionOrderId, setActionOrderId] = useState(null);

  const subTabs = ORDER_SUB_TABS[playTab] || [];
  const activeSubTab = resolveSubTab(playTab, subTab);
  const listType = activeSubTab?.listType || '';
  const symbolCodes = useMemo(() => collectSymbolCodes(records), [records]);
  const thumbs = useMarketThumbSocket(symbolCodes);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      orderApi.listTimedProducts(),
      orderApi.listFuturesProducts(),
      orderApi.listPerpetualProducts(),
    ])
      .then(([timed, futures, perpetual]) => {
        if (!cancelled) {
          setProductMaps({
            timed: buildProductMap(timed),
            futures: buildProductMap(futures),
            perpetual: buildProductMap(perpetual),
          });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setProductMaps({
            timed: new Map(),
            futures: new Map(),
            perpetual: new Map(),
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const loadOrders = useCallback(
    async (nextPlayTab, nextSubTab, pageNo, append = false) => {
      if (pageNo === 1) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      setError('');
      try {
        const data = await fetchOrderPage(nextPlayTab, nextSubTab, pageNo, PAGE_SIZE);
        const rows = data?.records || [];
        setRecords((prev) => (append ? [...prev, ...rows] : rows));
        setPage(data?.current || pageNo);
        setTotalPages(data?.pages || 1);
      } catch (err) {
        if (!append) {
          setRecords([]);
        }
        setError(err.message || t('orders.loadFailed'));
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [t],
  );

  useEffect(() => {
    loadOrders(playTab, subTab, 1, false);
  }, [playTab, subTab, loadOrders]);

  function handlePlayTabChange(nextPlayTab) {
    if (nextPlayTab === playTab) {
      return;
    }
    setPlayTab(nextPlayTab);
    setSubTab(getDefaultSubTab(nextPlayTab));
    setRecords([]);
    setPage(1);
    setTotalPages(1);
  }

  function handleSubTabChange(nextSubTab) {
    if (nextSubTab === subTab) {
      return;
    }
    setSubTab(nextSubTab);
    setRecords([]);
    setPage(1);
    setTotalPages(1);
  }

  function handleLoadMore() {
    if (loadingMore || page >= totalPages) {
      return;
    }
    loadOrders(playTab, subTab, page + 1, true);
  }

  function resolveThumb(order) {
    return thumbs[normalizeSymbolCode(order?.marketSymbolCode || '')];
  }

  function resolveCurrentPrice(order) {
    const thumb = resolveThumb(order);
    return thumb?.price ?? thumb?.close ?? order?.orderPrice;
  }

  async function handleFuturesCancel(order) {
    if (!order?.id) {
      return;
    }
    setActionOrderId(order.id);
    setError('');
    try {
      await orderApi.cancelFuturesOrder({ id: order.id });
      await loadOrders(playTab, subTab, 1, false);
      await refreshWallet();
    } catch (err) {
      setError(err.message || t('trade.cancelFailed'));
    } finally {
      setActionOrderId(null);
    }
  }

  async function handlePerpetualCancel(order) {
    if (!order?.orderSerialId) {
      return;
    }
    setActionOrderId(order.orderSerialId);
    setError('');
    try {
      await orderApi.cancelPerpetualOrder(order.orderSerialId);
      await loadOrders(playTab, subTab, 1, false);
      await refreshWallet();
    } catch (err) {
      setError(err.message || t('trade.cancelFailed'));
    } finally {
      setActionOrderId(null);
    }
  }

  async function handlePerpetualClose(order) {
    if (!order?.orderSerialId) {
      return;
    }
    setActionOrderId(order.orderSerialId);
    setError('');
    try {
      await orderApi.closePerpetualOrder(order.orderSerialId);
      await loadOrders(playTab, subTab, 1, false);
      await refreshWallet();
    } catch (err) {
      setError(err.message || t('orders.closeFailed'));
    } finally {
      setActionOrderId(null);
    }
  }

  function renderOrderItem(order) {
    const product = resolveOrderProduct(order, playTab, productMaps);
    const currentPrice = resolveCurrentPrice(order);
    const key = order.id || order.orderSerialId;

    if (listType === 'timedActive' || listType === 'timedHistory') {
      return (
        <TimedPositionCard
          key={key}
          order={order}
          product={product}
          currentPrice={currentPrice}
          onSettled={
            listType === 'timedActive'
              ? () => loadOrders(playTab, subTab, 1, false)
              : undefined
          }
        />
      );
    }

    if (listType === 'futuresActive') {
      return (
        <FuturesPositionCard
          key={key}
          order={order}
          product={product}
          currentPrice={currentPrice}
          cancelling={actionOrderId === order.id}
          onCancel={handleFuturesCancel}
        />
      );
    }

    if (listType === 'futuresHistory') {
      return (
        <FuturesPositionCard
          key={key}
          order={order}
          product={product}
          currentPrice={currentPrice}
        />
      );
    }

    if (listType === 'perpetualPending') {
      return (
        <PerpetualEntrustmentCard
          key={key}
          order={order}
          product={product}
          currentPrice={currentPrice}
          cancelling={actionOrderId === order.orderSerialId}
          onCancel={handlePerpetualCancel}
        />
      );
    }

    if (listType === 'perpetualOpen') {
      return (
        <PerpetualPositionCard
          key={key}
          order={order}
          product={product}
          currentPrice={currentPrice}
          balance={balance}
          closing={actionOrderId === order.orderSerialId}
          onClosePosition={handlePerpetualClose}
        />
      );
    }

    return (
      <WalletPositionHistoryItem
        key={key}
        order={order}
        tab="perpetual"
        t={t}
      />
    );
  }

  const listClassName =
    listType === 'perpetualPending' || listType === 'perpetualOpen'
      ? 'futures-trade-page__position-list'
      : 'orders-page__list';

  return (
    <div className="orders-page">
      <header className="orders-page__header">
        <button
          type="button"
          className="orders-page__back"
          aria-label={t('common.back')}
          onClick={() => navigate(-1)}
        >
          ‹
        </button>
        <h1>{t('orders.title')}</h1>
        <span className="orders-page__header-spacer" aria-hidden />
      </header>

      <div className="orders-page__primary-tabs perpetual-trade-page__tabs" role="tablist">
        {ORDER_PLAY_TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={playTab === tab.key}
            className={playTab === tab.key ? 'is-active' : ''}
            onClick={() => handlePlayTabChange(tab.key)}
          >
            {t(tab.labelKey)}
          </button>
        ))}
      </div>

      <div
        className={`orders-page__subtabs ${playTab === 'perpetual' ? 'orders-page__subtabs--perpetual' : ''}`}
        role="tablist"
      >
        {subTabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={subTab === tab.key}
            className={`orders-page__subtab ${subTab === tab.key ? 'is-active' : ''}`}
            onClick={() => handleSubTabChange(tab.key)}
          >
            {t(tab.labelKey)}
          </button>
        ))}
      </div>

      {loading && <p className="orders-page__status">{t('common.loading')}</p>}
      {error && <p className="orders-page__error">{error}</p>}

      {!loading && records.length === 0 && !error && (
        <div className="orders-page__empty">
          <div className="orders-page__empty-icon" aria-hidden>
            📄
          </div>
          <p>{t('common.noData')}</p>
        </div>
      )}

      {!loading && records.length > 0 && (
        <>
          <ul className={listClassName}>
            {records.map((order) => renderOrderItem(order))}
          </ul>

          {page < totalPages ? (
            <div className="orders-page__footer">
              <button
                type="button"
                className="orders-page__load-more"
                disabled={loadingMore}
                onClick={handleLoadMore}
              >
                {loadingMore ? t('common.loading') : t('funds.loadMore')}
              </button>
            </div>
          ) : (
            <p className="orders-page__footer">{t('funds.noMore')}</p>
          )}
        </>
      )}
    </div>
  );
}
